/**
 * Website display switches: whether a company's name and logo may appear on vera.inc.
 *
 * Three SURFACES, each with its own switch on the company record, because they are
 * different levels of exposure and a yes to one is not a yes to another:
 *
 *   clients   the website's clients page             companies.show_on_website_clients
 *   homepage  the homepage hero's logo marquee       companies.show_on_website_homepage
 *   industry  the industry pages matching the        companies.show_on_website_industry
 *             company's `categories`
 *
 * Plus CURATION: `companies.website_logo_priority` (nullable integer, lower = more
 * important). Spencer, 2026-10-09: "likely only show the most important logos on
 * homepage and industry pages". The public endpoint sorts each list by priority (nulls
 * last) then name and carries the number, so the website takes its own top N. N is not
 * decided here.
 *
 * ONE rule, read by the API's public endpoint (`GET /api/public/website/clients`), the
 * internal endpoint behind the switches, and the platform's internal Companies page.
 * It lives in @repo/types so the page and the endpoint cannot disagree about who shows.
 *
 * ── WHAT GIVES VERA THE RIGHT TO SHOW A LOGO ─────────────────────────────────────
 *
 * A logo is a third-party trademark. A switch only records that a Vera staffer clicked;
 * it is not the brand's permission. The permission comes from one of two places:
 *
 *   terms   the "Customer name and logo" paragraphs in section 9 of the Terms
 *           (packages/legal/terms-and-conditions.md, first in version
 *           PUBLICITY_CLAUSE_TERMS_VERSION; live 2026-10-09). Counted only
 *           when a CURRENT BRAND-ROLE MEMBER of the company has a
 *           `user_legal_acceptances` row for `terms` at that version or later. Brand
 *           sign-up records that row for the founding admin (the "I accept the Terms
 *           of Service" checkbox on the platform's sign-up, POST /signup/company).
 *   manual  no such acceptance is on record, so a staffer confirmed in the UI that the
 *           brand gave permission some other way (email, contract). Who confirmed and
 *           when is stored on the company (`website_permission_confirmed_by` / `_at`).
 *
 * Companies that signed up before the checkbox existed, or were created by staff, have
 * no acceptance on record, so for them the "manual" path is the one in use.
 *
 * Decided (Spencer, 2026-10-09): a trialing (not yet paying) company is NOT excluded
 * automatically. "If I set any of the show on website columns then it's fine to add
 * them." The switches are the only gate.
 *
 * ── WITHDRAWAL ───────────────────────────────────────────────────────────────────
 *
 * `publicity_opt_out` records the brand's written withdrawal. It beats everything: an
 * opted-out company is never eligible and its switches cannot be turned on.
 */
import {CompanyKind} from './company-kind.enum.js';

/** Live 2026-10-09. The first Terms version that carries the publicity clause. */
export const PUBLICITY_CLAUSE_TERMS_VERSION = '2026-10-09';

export const WEBSITE_DISPLAY_SURFACES = ['clients', 'homepage', 'industry'] as const;
export type WebsiteDisplaySurface = (typeof WEBSITE_DISPLAY_SURFACES)[number];

/** Which companies column holds each surface's switch. */
export const WEBSITE_DISPLAY_SURFACE_FIELD = {
  clients: 'show_on_website_clients',
  homepage: 'show_on_website_homepage',
  industry: 'show_on_website_industry',
} as const satisfies Record<WebsiteDisplaySurface, string>;

/** `website_logo_priority` bounds (inclusive). Lower = more important; null = unranked. */
export const WEBSITE_LOGO_PRIORITY_MIN = 0;
export const WEBSITE_LOGO_PRIORITY_MAX = 9999;

/** What the rule needs to know about a company. Field names match the companies columns. */
export interface WebsiteDisplayInput {
  show_on_website_clients: boolean;
  show_on_website_homepage: boolean;
  show_on_website_industry: boolean;
  publicity_opt_out: boolean;
  kind?: CompanyKind | `${CompanyKind}` | null;
  archived?: boolean | null;
  suspended?: boolean | null;
  is_example?: boolean | null;
  is_test?: boolean | null;
  /**
   * The highest `terms` version accepted by a current brand-role member of the company,
   * or null when none is on record. A Terms version is a `YYYY-MM-DD` string
   * (`TERMS_AND_CONDITIONS_VERSION` in @repo/legal), so string order is date order.
   */
  latest_member_terms_version: string | null;
}

export type PublicityClauseStatus = 'accepted' | 'not_recorded';

const TERMS_VERSION = /^\d{4}-\d{2}-\d{2}$/;

/** True for a well-formed Terms version string (`YYYY-MM-DD`). */
export function isTermsVersion(value: unknown): value is string {
  return typeof value === 'string' && TERMS_VERSION.test(value);
}

/**
 * `accepted` only for a well-formed version at or after the clause. Anything else,
 * including a malformed version string, is `not_recorded`: the rule must never read an
 * unparseable value as consent.
 */
export function publicityClauseStatus(latestMemberTermsVersion: string | null | undefined): PublicityClauseStatus {
  if (!isTermsVersion(latestMemberTermsVersion)) return 'not_recorded';
  return latestMemberTermsVersion >= PUBLICITY_CLAUSE_TERMS_VERSION ? 'accepted' : 'not_recorded';
}

/** Why a company is not shown, or cannot be switched on. */
export type WebsiteDisplayBlockReason =
  | 'switch_off'
  | 'opted_out'
  | 'not_a_client_company'
  | 'archived'
  | 'suspended'
  | 'example'
  | 'test';

export const WEBSITE_DISPLAY_REASON_TEXT: Record<WebsiteDisplayBlockReason, string> = {
  switch_off: 'The switch for this surface is off.',
  opted_out: 'This company has withdrawn permission to use its name and logo. Clear the opt-out only if it has given permission again in writing.',
  not_a_client_company: 'Demo and internal companies are never shown on the website.',
  archived: 'Archived companies are not shown on the website.',
  suspended: 'Suspended companies are not shown on the website.',
  example: 'Example companies are not shown on the website.',
  test: 'Test companies are not shown on the website.',
};

export const WEBSITE_DISPLAY_CLAUSE_WARNING =
  'No member of this company has accepted the Terms version that includes the publicity clause. Turn this on only if the company has given Vera permission to use its name and logo in writing.';

export type WebsiteDisplayToggleCheck =
  | {allowed: false; reason: WebsiteDisplayBlockReason}
  | {allowed: true; warning: 'clause_not_recorded' | null};

/**
 * May a staffer turn a surface's switch ON? Blocks what can never be shown (an opt-out,
 * a demo or internal company); warns, never blocks, when the clause is not on record
 * (a company that signed up before the Terms checkbox, or was created by staff).
 *
 * Archived and suspended companies may be switched on: the switch survives the state,
 * and the public endpoint leaves them out until the state clears.
 */
export function checkWebsiteDisplayToggle(company: WebsiteDisplayInput): WebsiteDisplayToggleCheck {
  if (company.publicity_opt_out) return {allowed: false, reason: 'opted_out'};
  if ((company.kind ?? CompanyKind.Standard) !== CompanyKind.Standard) {
    return {allowed: false, reason: 'not_a_client_company'};
  }
  const clause = publicityClauseStatus(company.latest_member_terms_version);
  return {allowed: true, warning: clause === 'accepted' ? null : 'clause_not_recorded'};
}

export type WebsiteDisplayBasis = 'terms' | 'manual';

export type WebsiteDisplayEligibility =
  | {eligible: false; reason: WebsiteDisplayBlockReason}
  | {eligible: true; basis: WebsiteDisplayBasis};

/**
 * Is this company shown on `surface` right now? The order is the precedence: the
 * brand's withdrawal first, then what kind of company it is, then its state, then the
 * switch. Eligible companies carry the basis for showing them.
 */
export function websiteDisplayEligibility(
  company: WebsiteDisplayInput,
  surface: WebsiteDisplaySurface,
): WebsiteDisplayEligibility {
  if (company.publicity_opt_out) return {eligible: false, reason: 'opted_out'};
  if ((company.kind ?? CompanyKind.Standard) !== CompanyKind.Standard) {
    return {eligible: false, reason: 'not_a_client_company'};
  }
  if (company.is_example) return {eligible: false, reason: 'example'};
  if (company.is_test) return {eligible: false, reason: 'test'};
  if (company.archived) return {eligible: false, reason: 'archived'};
  if (company.suspended) return {eligible: false, reason: 'suspended'};
  if (!company[WEBSITE_DISPLAY_SURFACE_FIELD[surface]]) return {eligible: false, reason: 'switch_off'};
  const clause = publicityClauseStatus(company.latest_member_terms_version);
  return {eligible: true, basis: clause === 'accepted' ? 'terms' : 'manual'};
}

/** Sort for every public list: priority ascending, unranked (null) last, then name. */
export function compareWebsiteLogoPriority(
  a: {priority: number | null; name: string},
  b: {priority: number | null; name: string},
): number {
  if (a.priority !== b.priority) {
    if (a.priority === null) return 1;
    if (b.priority === null) return -1;
    return a.priority - b.priority;
  }
  return a.name.localeCompare(b.name);
}
